import { useEffect, useState } from "react";
import { supabase } from "./supabase.js";

const BUCKET = "attachments";

/**
 * The bucket is private. Nothing here produces a durable public URL — files are
 * reached only through short-lived signed URLs, which is what lets driver's
 * licences and signed agreements live in the same storage as everything else.
 *
 * Requires storage-policies.sql to have been run; without it every upload fails
 * with "new row violates row-level security policy".
 */

// Generous compared with v10's 2.5 MB workaround, which existed because files
// were base64'd into the record itself. Phone photos are commonly 5-12 MB.
export const MAX_BYTES = 25 * 1024 * 1024;

const extOf = (name = "") => {
  const m = /\.([A-Za-z0-9]{1,8})$/.exec(name);
  return m ? m[1].toLowerCase() : "bin";
};

/** Upload and return the storage path to record on the owning row. */
export async function uploadFile(file, folder) {
  if (!file) throw new Error("No file selected.");
  if (file.size > MAX_BYTES) {
    throw new Error(
      `That file is ${(file.size / 1024 / 1024).toFixed(1)} MB. The limit is ` +
        `${MAX_BYTES / 1024 / 1024} MB — photograph it at a lower resolution, or attach a PDF.`
    );
  }

  const path = `${folder}/${crypto.randomUUID()}.${extOf(file.name)}`;
  const { error } = await supabase.storage.from(BUCKET).upload(path, file, {
    contentType: file.type || "application/octet-stream",
    upsert: false,
  });
  if (error) {
    if (/row-level security/i.test(error.message)) {
      throw new Error(
        "Storage refused the upload. storage-policies.sql has probably not been run " +
          "against this project — see the README."
      );
    }
    throw new Error(error.message);
  }
  return path;
}

export async function removeFile(path) {
  if (!path) return;
  const { error } = await supabase.storage.from(BUCKET).remove([path]);
  // A failed cleanup leaves an orphan file but must not block the user from
  // saving the row, so this reports rather than throws.
  if (error) console.warn(`Could not remove ${path}: ${error.message}`);
}

export async function signedUrl(path, seconds = 300) {
  if (!path) return null;
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, seconds);
  if (error) throw new Error(error.message);
  return data.signedUrl;
}

/**
 * Resolve a storage path to a viewable URL. Signed URLs expire, so this
 * re-signs when the path changes rather than caching indefinitely.
 */
export function useSignedUrl(path, seconds = 300) {
  const [state, setState] = useState({ url: null, error: null, loading: !!path });

  useEffect(() => {
    let live = true;
    if (!path) {
      setState({ url: null, error: null, loading: false });
      return;
    }
    setState({ url: null, error: null, loading: true });
    signedUrl(path, seconds)
      .then((url) => live && setState({ url, error: null, loading: false }))
      .catch((err) => live && setState({ url: null, error: err.message, loading: false }));
    return () => { live = false; };
  }, [path, seconds]);

  return state;
}

export const isImagePath = (path) => /\.(png|jpe?g|gif|webp|avif|heic|bmp)$/i.test(path ?? "");
